import { useMemo, useState, type FormEvent } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { createTaskRepository } from '@/data/repositories/task-repository';
import type { TaskPriority, Uuid } from '@/data/db';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { Field, Select, TextInput } from '@/components/primitives/field';
import { useToast } from '@/components/feedback/toast';
import { useBoardColumns } from '@/features/tasks/use-tasks';
import { cn } from '@/lib/cn';

type CaptureMode = 'task' | 'note';

const PRIORITY_OPTIONS: ReadonlyArray<{ value: TaskPriority; label: string }> = [
  { value: 'normal', label: '一般' },
  { value: 'important', label: '重要' },
  { value: 'urgent', label: '緊急' },
];

interface QuickCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 建立後開啟詳細抽屜。 */
  onCreated?: (taskId: Uuid) => void;
}

/**
 * 快速新增（spec 9.3）。標題是唯一必填；其餘欄位順手填。
 * 工作與便條共用入口，但兩種模式必須清楚區分（spec 6.1）。
 */
export function QuickCaptureDialog({ open, onOpenChange, onCreated }: QuickCaptureDialogProps) {
  const { db, save, settings } = useWorkspace();
  const toast = useToast();
  const columns = useBoardColumns();
  const tasks = useMemo(() => createTaskRepository(db), [db]);

  const [mode, setMode] = useState<CaptureMode>('task');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [pickedColumnId, setPickedColumnId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const normalColumns = useMemo(
    () =>
      (columns ?? []).filter((column) => column.kind === 'normal' && column.archivedAt === null),
    [columns],
  );

  // 對話框關閉時整個卸載，所以預設值在 render 期間推導就好，不需要 effect 同步。
  // 預設欄位是設定頁指定的那一欄；種子資料指向最左側的「提醒老闆」（spec 13.1）。
  const defaultColumnId = settings?.defaultBoardColumnId ?? normalColumns[0]?.id ?? '';
  const columnId = pickedColumnId ?? defaultColumnId;
  const defaultColumnName = normalColumns.find((column) => column.id === defaultColumnId)?.name;

  async function submit(event: FormEvent, thenOpenDetail: boolean) {
    event.preventDefault();
    const trimmed = subject.trim();
    if (!trimmed) {
      setError('請先寫下標題，其他欄位可以之後再補。');
      return;
    }

    setBusy(true);
    try {
      let createdId: Uuid | null = null;
      await save.run(async () => {
        const created = await tasks.create({
          subject: trimmed,
          kind: mode,
          priority,
          dueDate: dueDate || null,
          ...(columnId ? { columnId } : {}),
        });
        createdId = created.id;
      });

      toast.show({
        message: mode === 'task' ? '已建立工作' : '已建立便條',
        detail: trimmed,
        tone: 'success',
      });

      if (thenOpenDetail && createdId) {
        onCreated?.(createdId);
      }
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '沒有存成功，可以再試一次。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="快速新增"
      description="只要寫下標題就能建立，其他欄位可以之後再補。"
      size="md"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={(event) => void submit(event, true)}
          >
            建立並開啟
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={(event) => void submit(event, false)}
          >
            建立
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void submit(event, false)}>
        <fieldset>
          <legend className="sr-only">要新增什麼</legend>
          <div className="inline-flex rounded-full bg-surface-sunken p-1">
            {(
              [
                { value: 'task', label: '工作' },
                { value: 'note', label: '便條' },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={mode === option.value}
                onClick={() => setMode(option.value)}
                className={cn(
                  'rounded-full px-4 py-1.5 text-[0.8125rem] transition-colors duration-200',
                  mode === option.value
                    ? 'bg-surface-raised font-medium text-text shadow-[var(--shadow-ambient)]'
                    : 'text-text-muted hover:text-text',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <Field label="標題" error={error}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={subject}
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- 快速記錄的重點就是打開就能打字
              autoFocus
              placeholder={mode === 'task' ? '例如：提醒執行長確認季報' : '例如：記得帶印章'}
              onChange={(event) => {
                setSubject(event.target.value);
                if (error) setError(null);
              }}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="到期日">
            {({ id }) => (
              <TextInput
                id={id}
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            )}
          </Field>

          <Field label="重要程度">
            {({ id }) => (
              <Select
                id={id}
                value={priority}
                onValueChange={(value) => setPriority(value as TaskPriority)}
                options={PRIORITY_OPTIONS}
              />
            )}
          </Field>
        </div>

        {normalColumns.length > 0 && (
          <Field
            label="看板欄位"
            {...(defaultColumnName
              ? { hint: `預設放進「${defaultColumnName}」，要換到設定頁改新任務預設欄位。` }
              : {})}
          >
            {({ id }) => (
              <Select
                id={id}
                value={columnId}
                onValueChange={setPickedColumnId}
                options={normalColumns.map((column) => ({
                  value: column.id,
                  label: column.name,
                }))}
              />
            )}
          </Field>
        )}
      </form>
    </Dialog>
  );
}
